import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  UserPlus,
  Info,
  Share2,
  Hammer,
  XCircle,
  StopCircle,
  Shield,
  MessageSquare,
  UserCheck,
  Check,
} from 'lucide-react'
import {
  listNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  type Notification,
} from '../lib/api'
import { formatRelativeTime } from '../lib/format'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { formatNotificationMessage } from '../lib/notificationFormat'
import { getNotificationLink } from '../lib/notificationLinks'
import { useAutoMarkRead } from '../hooks/useAutoMarkRead'
import { useNotificationCount } from '../hooks/useNotificationCount'

interface TypeConfig {
  icon: React.ElementType
  color: string
  isAdmin?: boolean
}

const typeConfig: Record<string, TypeConfig> = {
  // User notifications
  app_shared: { icon: Share2, color: 'text-blue-500' },
  access_request: { icon: UserPlus, color: 'text-purple-500' },
  access_approved: { icon: UserCheck, color: 'text-green-500' },
  access_denied: { icon: XCircle, color: 'text-red-500' },
  production_build_triggered: { icon: Hammer, color: 'text-amber-500' },
  production_build_success: { icon: CheckCircle2, color: 'text-green-500' },
  production_build_failed: { icon: XCircle, color: 'text-red-500' },
  app_error: { icon: AlertTriangle, color: 'text-red-500' },
  app_auto_stopped: { icon: StopCircle, color: 'text-muted-foreground' },
  // Admin notifications
  new_user_registered: { icon: UserPlus, color: 'text-blue-500', isAdmin: true },
  secret_scan_finding: { icon: Shield, color: 'text-red-500', isAdmin: true },
  new_feedback: { icon: MessageSquare, color: 'text-purple-500', isAdmin: true },
  feedback_comment: { icon: MessageSquare, color: 'text-blue-500' },
  feedback_status: { icon: CheckCircle2, color: 'text-green-500' },
}

function getConfig(type: string): TypeConfig {
  return typeConfig[type] || { icon: Info, color: 'text-muted-foreground' }
}


// Bell button + popup
export function NotificationBell() {
  const navigate = useNavigate()
  const { unreadCount, setUnreadCount } = useNotificationCount()
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const bellRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const [bellPulse, setBellPulse] = useState(false)
  const prevCountRef = useRef(0)

  const handleAutoMarkedRead = useCallback((ids: number[]) => {
    const idSet = new Set(ids)
    setNotifications(prev => prev.map(n => idSet.has(n.id) ? { ...n, is_read: true } : n))
    setUnreadCount(prev => Math.max(0, prev - ids.length))
  }, [setUnreadCount])

  const { observe } = useAutoMarkRead(handleAutoMarkedRead, 2000, listRef.current)

  const loadCount = useCallback(async () => {
    try {
      const { count } = await getUnreadCount()
      setUnreadCount(prev => {
        // Trigger pulse when count increases (new notification arrived)
        if (count > prev && prev !== 0) {
          setBellPulse(true)
          setTimeout(() => setBellPulse(false), 1000)
        }
        prevCountRef.current = prev
        return count
      })
    } catch {
      // Don't re-throw — unread count is lightweight, keep polling at base interval
    }
  }, [setUnreadCount])

  useAutoRefresh(loadCount, { baseInterval: 10000, maxInterval: 60000 })

  const loadNotifications = useCallback(async () => {
    setLoading(true)
    try {
      const data = await listNotifications({ limit: 50 })
      setNotifications(data)
    } catch {
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) loadNotifications()
  }, [open, loadNotifications])

  // Click outside to close
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Escape to close
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  async function handleMarkAllRead() {
    try {
      await markAllNotificationsRead()
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0) // context-synced
    } catch {
      // silently fail
    }
  }

  async function handleMarkRead(id: number) {
    try {
      await markNotificationRead(id)
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
      setUnreadCount((prev: number) => Math.max(0, prev - 1)) // context-synced
    } catch {
      // silently fail
    }
  }

  const hasUnread = notifications.some(n => !n.is_read)

  // Position popup: below bell on mobile (bell is at top), above bell on desktop (bell is at bottom)
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({})
  useEffect(() => {
    if (!open || !bellRef.current) return
    const rect = bellRef.current.getBoundingClientRect()
    const isMobile = window.innerWidth < 768
    const panelWidth = Math.min(360, window.innerWidth - 16)

    if (isMobile) {
      // Bell is in the top header — open popup below it
      const left = Math.min(rect.right - panelWidth, Math.max(8, rect.left))
      setPopupStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left,
        width: panelWidth,
        maxHeight: `calc(100vh - ${rect.bottom + 16}px)`,
        zIndex: 9999,
      })
    } else {
      // Bell is in the sidebar bottom — open popup above it
      setPopupStyle({
        position: 'fixed',
        left: rect.left,
        bottom: window.innerHeight - rect.top + 8,
        width: 360,
        zIndex: 9999,
      })
    }
  }, [open])

  return (
    <div className="relative">
      <button
        ref={bellRef}
        onClick={() => setOpen(!open)}
        className={`relative p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground ${bellPulse ? 'animate-bell-pulse' : ''}`}
        title="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={popupStyle}
          className="max-h-[480px] bg-card border border-border rounded-xl shadow-2xl flex flex-col animate-in fade-in duration-150"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <h3 className="text-sm font-semibold">Notifications</h3>
            {hasUnread && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Check className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div ref={listRef} className="flex-1 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-6 h-6 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              <>
                {notifications.slice(0, 6).map(notif => {
                  const cfg = getConfig(notif.type)
                  const Icon = cfg.icon
                  return (
                    <div
                      key={notif.id}
                      ref={!notif.is_read ? (el) => observe(notif.id, el) : undefined}
                      onClick={() => {
                        if (!notif.is_read) handleMarkRead(notif.id)
                        const link = getNotificationLink(notif)
                        if (link) { setOpen(false); navigate(link) }
                      }}
                      className={`flex items-center gap-2.5 px-3 py-2.5 border-b border-border/50 transition-colors cursor-pointer ${
                        !notif.is_read
                          ? 'bg-primary/[0.03] hover:bg-primary/[0.06]'
                          : 'hover:bg-secondary/30'
                      }`}
                    >
                      {!notif.is_read && (
                        <div className="shrink-0">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        </div>
                      )}
                      <div className={`shrink-0 ${cfg.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <p className={`flex-1 min-w-0 text-[13px] leading-snug line-clamp-2 ${!notif.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {formatNotificationMessage(notif)}
                        {cfg.isAdmin && (
                          <span className="ml-1.5 inline-flex px-1 py-0.5 text-[9px] font-semibold bg-amber-500/20 text-amber-600 dark:text-amber-400 rounded align-middle">
                            Admin
                          </span>
                        )}
                      </p>
                      <span className="shrink-0 text-[11px] text-muted-foreground/70 tabular-nums">
                        {formatRelativeTime(notif.created_at)}
                      </span>
                    </div>
                  )
                })}
              </>
            )}
          </div>

          {/* Sticky footer — always visible */}
          <button
            onClick={() => { setOpen(false); navigate('/notifications') }}
            className="shrink-0 w-full px-4 py-3 text-sm text-primary font-medium hover:bg-secondary/30 transition-colors text-center border-t border-border rounded-b-xl"
          >
            {notifications.length > 6
              ? `View all ${notifications.length} notifications`
              : 'View all notifications'}
          </button>
        </div>,
        document.body
      )}

    </div>
  )
}

// Keep default export for backwards compatibility
export default function NotificationPanel({ open }: { open: boolean; onClose?: () => void }) {
  // Deprecated — NotificationBell now contains the popup inline
  if (!open) return null
  return null
}
